import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Mic, Square, Sparkles } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import VoiceButton from "@/components/VoiceButton";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

const EMPTY = {
  first_name: "", middle_names: "", last_name: "", dob: "", address: "", phone: "",
  email: "", nhs_number: "", emergency_contact_name: "", emergency_contact_phone: "",
  allergies: "", event_location: "", venue: "Medical Centre", arrived_at: "", w3w: "",
};

function toLocalInput(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d)) return "";
  d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
  return d.toISOString().slice(0, 16);
}

export default function AddPatientDialog({ open, onOpenChange, onSaved, patient }) {
  const editing = Boolean(patient);
  const [form, setForm] = useState(() =>
    patient
      ? { ...EMPTY, ...patient, allergies: (patient.allergies || []).join(", "), arrived_at: toLocalInput(patient.arrived_at) }
      : { ...EMPTY, arrived_at: toLocalInput(new Date().toISOString()) }
  );
  const [saving, setSaving] = useState(false);
  const [events, setEvents] = useState([]);
  const [showAllEvents, setShowAllEvents] = useState(false);
  const [dictating, setDictating] = useState(false);
  const [dictBusy, setDictBusy] = useState(false);
  const [dictTranscript, setDictTranscript] = useState("");
  const mediaRef = useRef(null);
  const chunksRef = useRef([]);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const toggleDictation = async () => {
    if (dictating) {
      mediaRef.current?.stop();
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream);
      chunksRef.current = [];
      mr.ondataavailable = (e) => chunksRef.current.push(e.data);
      mr.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        setDictating(false);
        const blob = new Blob(chunksRef.current, { type: mr.mimeType || "audio/webm" });
        const fd = new FormData();
        fd.append("file", blob, "dictation.webm");
        setDictBusy(true);
        try {
          const { data } = await api.post("/patients/parse-dictation", fd);
          setDictTranscript(data.transcript || "");
          if (data.warning) {
            toast.warning(data.warning);
          } else {
            setForm((f) => ({ ...f, ...data.fields }));
            const n = Object.keys(data.fields || {}).length;
            toast.success(n ? `Filled ${n} field${n > 1 ? "s" : ""} — check them before saving` : "Nothing recognised — try again or type");
          }
        } catch (err) {
          toast.error(formatApiError(err));
        } finally {
          setDictBusy(false);
        }
      };
      mediaRef.current = mr;
      mr.start();
      setDictating(true);
      setDictTranscript("");
    } catch {
      toast.error("Microphone access denied — allow the mic in your browser");
    }
  };

  const td = new Date();
  const todayStr = `${td.getFullYear()}-${String(td.getMonth() + 1).padStart(2, "0")}-${String(td.getDate()).padStart(2, "0")}`;
  const todaysEvents = events.filter((ev) => ev.event_date && ev.event_date <= todayStr && todayStr <= (ev.end_date || ev.event_date));
  const pickableEvents = showAllEvents ? events : todaysEvents;

  useEffect(() => {
    if (open) {
      api.get("/events").then((r) => {
        const evs = r.data.filter((e) => e.id);
        setEvents(evs);
        if (!patient) {
          const todays = evs.filter((ev) => ev.event_date && ev.event_date <= todayStr && todayStr <= (ev.end_date || ev.event_date));
          if (todays.length === 1) {
            setForm((f) => (f.event_location ? f : { ...f, event_location: todays[0].name }));
          }
        }
      }).catch(() => {});
    }
  }, [open]);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = {
      ...form,
      allergies: form.allergies.split(",").map((a) => a.trim()).filter(Boolean),
      arrived_at: form.arrived_at ? new Date(form.arrived_at).toISOString() : "",
    };
    try {
      if (editing) await api.patch(`/patients/${patient.id}`, payload);
      else await api.post("/patients", payload);
      toast.success(editing ? "Patient updated" : "Patient added");
      onSaved();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const field = (label, key, opts = {}) => (
    <div key={key} className={opts.full ? "sm:col-span-2" : ""}>
      <label className="flex items-center justify-between text-xs font-semibold text-slate-600 mb-1">
        <span>{label}{opts.required && " *"}</span>
        <VoiceButton testid={`patient-voice-${key}`}
          onTranscript={(t) => setForm((f) => ({ ...f, [key]: f[key] ? `${f[key]} ${t}` : t }))} />
      </label>
      {opts.textarea ? (
        <textarea data-testid={`patient-input-${key}`} value={form[key]} onChange={set(key)} rows={2}
          className="w-full rounded-lg border border-slate-300 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#005EB8]" />
      ) : (
        <input data-testid={`patient-input-${key}`} type={opts.type || "text"} required={opts.required}
          value={form[key]} onChange={set(key)} placeholder={opts.placeholder || ""}
          className="w-full h-12 rounded-lg border border-slate-300 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#005EB8]" />
      )}
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl text-[#0F2547]">{editing ? "Edit Patient Details" : "Add New Patient"}</DialogTitle>
          <DialogDescription className="text-sm text-slate-500">
            Collect full patient demographics for the clinical record.
          </DialogDescription>
        </DialogHeader>
        {!editing && (
          <div className="mt-1">
            <button type="button" onClick={toggleDictation} disabled={dictBusy} data-testid="dictate-patient-btn"
              className={`w-full h-12 rounded-lg text-sm font-bold inline-flex items-center justify-center gap-2 transition-colors disabled:opacity-60 ${
                dictating ? "bg-[#D5281B] text-white animate-pulse" : "bg-[#007F3B] hover:bg-[#006631] text-white"}`}>
              {dictating ? <><Square className="h-4 w-4" /> Listening… tap to stop</>
                : dictBusy ? "Filling in the form…"
                : <><Mic className="h-4 w-4" /> Dictate everything <Sparkles className="h-4 w-4 opacity-70" /></>}
            </button>
            <p className="text-[11px] text-slate-400 mt-1">
              Say it all in one go, e.g. "John Smith, born 12th March 1988, lives at 12 High Street Cardiff, phone 07700 900123, allergic to penicillin" — AI fills the form. Or tap the mic on any single field.
            </p>
            {dictTranscript && (
              <p className="text-[11px] text-slate-500 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 mt-2" data-testid="dictation-transcript">
                Heard: "{dictTranscript}"
              </p>
            )}
          </div>
        )}
        <form onSubmit={submit} className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-2" data-testid="add-patient-form">
          {field("First name", "first_name", { required: true })}
          {field("Last name", "last_name", { required: true })}
          {field("Middle name(s)", "middle_names")}
          {field("Date of birth", "dob", { type: "date" })}
          {field("Arrival time", "arrived_at", { type: "datetime-local" })}
          {field("Telephone number", "phone", { type: "tel" })}
          {field("Email", "email", { type: "email" })}
          {field("Home address", "address", { textarea: true, full: true })}
          {field("NHS number", "nhs_number")}
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1">Event (today's events)</label>
            <select data-testid="patient-input-event_location" value={form.event_location}
              onChange={(e) => {
                const name = e.target.value;
                const match = events.find((ev) => ev.name === name);
                setForm((f) => ({ ...f, event_location: name, venue: f.venue || match?.venue || "" }));
              }}
              className="w-full h-12 rounded-lg border border-slate-300 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#005EB8] bg-white">
              <option value="">No event / unassigned</option>
              {pickableEvents.map((ev) => (
                <option key={ev.id} value={ev.name}>
                  {ev.name}{ev.event_date ? ` — ${ev.event_date}` : ""}
                </option>
              ))}
              {form.event_location && !events.some((ev) => ev.name === form.event_location) && (
                <option value={form.event_location}>{form.event_location} (no longer on calendar)</option>
              )}
            </select>
            <button type="button" onClick={() => setShowAllEvents((v) => !v)} data-testid="toggle-all-events"
              className="mt-1 text-[11px] font-semibold text-[#005EB8] hover:underline">
              {showAllEvents ? "Show only today's events" : `Show all events (${events.length})`}
            </button>
          </div>
          {field("Venue", "venue", { placeholder: "e.g. Cardiff City Stadium" })}
          {field("Emergency contact name", "emergency_contact_name")}
          {field("Emergency contact phone", "emergency_contact_phone", { type: "tel" })}
          {field("Allergies (comma separated)", "allergies", { full: true, placeholder: "e.g. Penicillin, Peanuts" })}
          {field("what3words location", "w3w", { full: true, placeholder: "e.g. ///apples.oranges.pears — where the patient was found" })}
          <div className="sm:col-span-2 flex justify-end gap-3 pt-2">
            <button type="button" onClick={() => onOpenChange(false)}
              className="h-12 px-5 rounded-lg border border-slate-300 text-sm font-semibold text-slate-600 hover:border-slate-400 transition-colors">
              Cancel
            </button>
            <button type="submit" disabled={saving} data-testid="add-patient-submit-button"
              className="h-12 px-6 rounded-lg bg-[#005EB8] hover:bg-[#004a93] transition-colors text-white text-sm font-semibold disabled:opacity-60">
              {saving ? "Saving…" : editing ? "Save changes" : "Add patient"}
            </button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
